import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { MAPS_EMBED_URL } from "@/lib/assets";
import useSEO from "@/hooks/useSEO";
import { GoogleReviewCard } from "@/components/GoogleReviews";

const ADDRESS = "2nd floor, Carlton Square, Near Madhavdeep Complex, Kalanala, Bhavnagar 364001";

export default function Contact() {
  useSEO({
    title: "Contact · Visit, Call or Write — Bhavnagar",
    description: "Reach Manas Neuropsychiatry Hospital — 2nd floor, Carlton Square, Kalanala, Bhavnagar 364001. Phone +91 94275 60101. Emergency 24×7.",
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in name, email, subject, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Your message has been sent. We'll respond shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16">
        <p className="eyebrow text-[#A39171] mb-6">Contact</p>
        <h1 className="font-display text-5xl md:text-7xl text-[#2D2623] leading-[0.95] tracking-tight max-w-4xl">
          Reach out — <span className="italic text-[#E07A5F]">we're listening</span>.
        </h1>
        <p className="mt-8 text-xl text-[#5C524E] max-w-2xl leading-relaxed">
          Call, write, or come by. Whichever feels right, we will respond with care.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Contact form */}
          <div className="lg:col-span-7 bg-[#F4EFE6] rounded-3xl p-8 md:p-12 border border-[#A39171]/15">
            <h2 className="font-display text-3xl md:text-4xl text-[#2D2623] tracking-tight">Send us a message</h2>
            <p className="text-[#5C524E] mt-3">We respond to non-emergencies within one business day.</p>

            <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Name *</Label>
                <Input data-testid="contact-name" value={form.name} onChange={(e) => setField("name", e.target.value)}
                  className="bg-white/70 border-[#A39171]/30 rounded-xl h-12 focus-visible:ring-[#E07A5F]/50" />
              </div>
              <div>
                <Label className="text-sm text-[#5C524E] mb-2 block">Phone</Label>
                <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)}
                  className="bg-white/70 border-[#A39171]/30 rounded-xl h-12 focus-visible:ring-[#E07A5F]/50" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm text-[#5C524E] mb-2 block">Email *</Label>
                <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)}
                  className="bg-white/70 border-[#A39171]/30 rounded-xl h-12 focus-visible:ring-[#E07A5F]/50" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm text-[#5C524E] mb-2 block">Subject *</Label>
                <Input data-testid="contact-subject" value={form.subject} onChange={(e) => setField("subject", e.target.value)}
                  className="bg-white/70 border-[#A39171]/30 rounded-xl h-12 focus-visible:ring-[#E07A5F]/50" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm text-[#5C524E] mb-2 block">Message *</Label>
                <Textarea data-testid="contact-message" value={form.message} onChange={(e) => setField("message", e.target.value)}
                  className="bg-white/70 border-[#A39171]/30 rounded-xl min-h-[140px] focus-visible:ring-[#E07A5F]/50" />
              </div>
              <div className="md:col-span-2">
                <Button data-testid="contact-submit" type="submit" disabled={submitting}
                  className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-12 font-medium w-full md:w-auto">
                  {submitting ? "Sending…" : (<><Send className="mr-2 w-4 h-4" /> Send message</>)}
                </Button>
              </div>
            </form>
          </div>

          {/* Side details */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-[#FDFBF7] rounded-3xl border border-[#A39171]/15 p-8">
              <p className="eyebrow text-[#A39171] mb-4">Direct lines</p>
              <ul className="space-y-5">
                <li className="flex items-start gap-4">
                  <span className="w-11 h-11 rounded-2xl bg-[#E07A5F]/15 flex items-center justify-center shrink-0">
                    <Phone className="w-4 h-4 text-[#E07A5F]" />
                  </span>
                  <div>
                    <p className="text-sm text-[#A39171]">Phone & Emergency</p>
                    <a href="tel:+919427560101" data-testid="contact-phone-link" className="text-[#2D2623] font-medium hover:text-[#E07A5F]">+91 94275 60101</a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-11 h-11 rounded-2xl bg-[#E07A5F]/15 flex items-center justify-center shrink-0">
                    <Mail className="w-4 h-4 text-[#E07A5F]" />
                  </span>
                  <div>
                    <p className="text-sm text-[#A39171]">Email</p>
                    <a href="mailto:manasneuropsychiatryhospital@gmail.com" data-testid="contact-email-link" className="text-[#2D2623] font-medium hover:text-[#E07A5F] break-all">
                      manasneuropsychiatryhospital@gmail.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-11 h-11 rounded-2xl bg-[#E07A5F]/15 flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-[#E07A5F]" />
                  </span>
                  <div>
                    <p className="text-sm text-[#A39171]">Visit us</p>
                    <p className="text-[#2D2623] font-medium leading-relaxed">{ADDRESS}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-11 h-11 rounded-2xl bg-[#E07A5F]/15 flex items-center justify-center shrink-0">
                    <Clock className="w-4 h-4 text-[#E07A5F]" />
                  </span>
                  <div>
                    <p className="text-sm text-[#A39171]">Visit hours</p>
                    <p className="text-[#2D2623] font-medium">Mon – Sat: 09:00 AM – 08:00 PM</p>
                    <p className="text-[#2D2623] font-medium">For Emergency: 24 × 7 Open</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-[#2D2623] text-[#FDFBF7] rounded-3xl p-8 relative overflow-hidden">
              <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
              <p className="eyebrow text-[#E2C044] mb-3">In crisis?</p>
              <p className="font-display text-2xl leading-tight">
                If you or someone you love is in a mental-health emergency, call us immediately.
              </p>
              <a href="tel:+919427560101" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#E07A5F] hover:bg-[#FDFBF7] hover:text-[#2D2623] px-6 h-11 font-medium transition-colors">
                <Phone className="w-4 h-4" /> +91 94275 60101
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Google review ask */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <GoogleReviewCard />
        </div>
      </section>

      {/* Map */}
      <section className="pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">Find us</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#2D2623] tracking-tight mb-8">
            We're a short drive from anywhere in Bhavnagar.
          </h2>
          <div className="rounded-3xl overflow-hidden border border-[#A39171]/15 aspect-[16/9] md:aspect-[21/9] bg-[#F4EFE6]">
            <iframe
              data-testid="contact-map"
              title="Manas Neuropsychiatry Hospital location"
              src={MAPS_EMBED_URL}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>
    </div>
  );
}
